import { type Dispatch } from "@reduxjs/toolkit";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import groupsDataService from "features/People/services/groupsDataService";
import { type DistributedOpUpdateParams, type UpdateParams } from "interfaces";
import enrollmentRulesUtils from "utils/enrollmentRulesUtils";
import { isForbidden } from "utils/statusCodeUtils";
import {
  fetchBegin,
  fetchFailure,
  fetchForbidden,
  fetchSuccess,
  type GroupInfo,
  type GroupSettings,
  updateEnrollmentRulesSuccess,
  updateGroupInfo,
} from "../slices/editGroupInfoSlice";

export const updateGroupEnrollmentRules = (groupId: number, enrollmentRules: any[]) => async (dispatch: Dispatch) => {
  try {
    const getOperationProps = async () => {
      const enrollmentRulesInServerFormat = enrollmentRulesUtils.mapRulesToSeverFormat(enrollmentRules);
      const response = await groupsDataService.updateGroupEnrollmentRules(groupId, enrollmentRulesInServerFormat);
      return response.data;
    };

    const updateParams: DistributedOpUpdateParams = {
      getOperationProps,
      indeterminate: true,
      id: `UpdateGroupEnrollmentRules_${groupId}`,
      title: "Update group enrollment rules",
      successTransientMessage: "Group enrollment rules updated successfully!",
      failureTransientMessage: "Failed to update group enrollment rules",
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
    dispatch(updateEnrollmentRulesSuccess(enrollmentRules));
  } catch (error) {}
};

export const fetchGroupInfo = (groupId: number) => async (dispatch: Dispatch) => {
  try {
    dispatch(fetchBegin());
    const groupInfo = await groupsDataService.getGroupInfo(groupId);
    dispatch(fetchSuccess(groupInfo.data));
  } catch (error) {
    isForbidden(error?.response?.status) ? dispatch(fetchForbidden(error)) : dispatch(fetchFailure(error));
  }
};

export const saveGroupInfo =
  (groupId: number, groupInfo: Partial<GroupInfo>, groupSettings: GroupSettings) => async (dispatch: Dispatch) => {
    try {
      const getMessageIds = async () => {
        const updateRequestPayload = {
          ...groupInfo,
          ...groupSettings,
        };

        const response = await groupsDataService.updateGroupInfo(groupId, updateRequestPayload);
        return [response.data.messageId];
      };

      const updateParams: UpdateParams = {
        getMessageIds,
        indeterminate: true,
        id: "UpdateGroupInformation",
        title: "Update group information",
        successTransientMessage: "Group information update succeeded!",
        failureTransientMessage: "Group information update failed!",
      };

      await backgroundTask.initializeBackgroundTask(updateParams, dispatch);
      dispatch(updateGroupInfo({ info: groupInfo, settings: groupSettings }));
    } catch (error) {}
  };
